import { runLiquidBannedPatternGate } from "@hightouch/sdk/lib/liquid-template/security";
// This module is the ONLY place allowed to import the engine directly —
// everything else must go through this wrapper so the fs lockdown and the
// parse-time banned-pattern gate below can't be skipped.
import {
  Liquid as _Liquid,
  Context,
  type FilterImplOptions,
  type Template,
  Tokenizer,
  TypeGuards,
  type TagToken,
  type Token,
  type TopLevelToken,
  type Value,
  type Expression,
  type Hash,
  type PropertyAccessToken,
  type QuotedToken,
  type RangeToken,
  type Emitter,
  type Tag,
  // biome-ignore lint/style/noRestrictedImports: we need to import liquidjs to wrap it and prevent all other code from using it.
} from "liquidjs";
// `IdentifierToken` is the one token sub-type liquidjs 10 still does not
// re-export from its main entry point (the others above are), so it needs a deep
// import. Reachable at runtime only via the `TypeGuards.isWordToken` predicate.
import type { IdentifierToken } from "liquidjs/dist/tokens/identifier-token";
// `TagImplOptions` moved off the main entry point in liquidjs 10 (its adapter
// wraps an options-style tag into a Tag subclass), so it also needs a deep import.
import type { TagImplOptions } from "liquidjs/dist/template/tag-options-adapter";

// liquidjs 10 dropped the v9 `TagImpl` interface. A registered options-style tag
// executes with the adapter-built `Tag` instance as `this`, carrying the options'
// own fields — this alias is that `this` type.
type TagImpl = Tag & TagImplOptions;

// This can be removed once https://github.com/hightouchio/hightouch/pull/21735 is merged.
type LiquidOptions = ConstructorParameters<typeof _Liquid>[0];

export class Liquid extends _Liquid {
  constructor(options?: LiquidOptions) {
    super({
      ...options,

      // Relative includes ({% include %}/{% render %} with a relative path) are
      // the only feature that drives the engine to touch the fs adapter, which is
      // locked down below — so this is both the security-correct default and an
      // exact match for liquidjs 9, which had no such option at all. On 10+ it
      // defaults to `true` and the engine console.warns once per construction
      // when the fs adapter lacks dirname/sep (ours intentionally omits them),
      // which at sync volume floods stderr as errors. Mirrors the v10 engines in
      // sdk/lib/liquid-template/liquid.ts.
      relativeReference: false,

      // Don't allow templates to access file system.
      // See https://www.hacefresko.com/posts/liquidjs-ssti-to-arbitrary-file-read
      // and https://github.com/harttle/liquidjs/pull/138.
      fs: {
        readFileSync(filepath) {
          throw new Error(
            "Unauthorized attempt to access path `" +
              filepath +
              "` via Liquid template",
          );
        },
        async readFile(filepath) {
          throw new Error(
            "Unauthorized attempt to access path `" +
              filepath +
              "` via Liquid template",
          );
        },
        existsSync(filepath) {
          throw new Error(
            "Unauthorized attempt to access path `" +
              filepath +
              "` via Liquid template",
          );
        },
        async exists(filepath) {
          throw new Error(
            "Unauthorized attempt to access path `" +
              filepath +
              "` via Liquid template",
          );
        },
        // `contains` exists on the FS interface as of v9.28.3
        // (https://github.com/harttle/liquidjs/commit/aebeae9e1bbb8472af7788dfd09a08cb6de58e1c)
        // and so is available on the 10.x engine this wraps. Left unimplemented
        // deliberately: omitting it makes the loader assume "contained" and fall
        // through to `existsSync`/`readFileSync`, which already throw, so adding
        // it would not change behavior.
        // contains(root, file) {
        //   throw new Error(
        //     "Unauthorized attempt to access path `" +
        //       root +
        //       file +
        //       "` via Liquid template",
        //   );
        // },
        resolve(dir, file, ext) {
          throw new Error(
            "Unauthorized attempt to access file `" +
              file +
              ext +
              "` in directory `" +
              dir +
              "` via Liquid template",
          );
        },
      },
    });

    // Banned-pattern gate at the single string→template chokepoint:
    // parseAndRender / parseAndRenderSync / parseFile all route through
    // `this.parse` (verified in liquidjs v9 and v10), while render() /
    // renderSync() take an already-parsed Template[]. Patched at the instance
    // level — not via a class-level `override` — because the frontend's
    // tsconfig sets `noImplicitOverride: true`, which forces tsgo to fully
    // analyze the upstream Liquid class type and OOMs on the
    // `packages/app/tsconfig.json` check. The gate is a no-op until the
    // backend registers hooks, so the frontend is unaffected.
    const origParse = this.parse.bind(this);
    this.parse = (html: string, filepath?: string) => {
      runLiquidBannedPatternGate(html, "parse");
      return origParse(html, filepath);
    };
  }
}

export type {
  FilterImplOptions,
  TagToken,
  Template,
  Token,
  TopLevelToken,
  PropertyAccessToken,
  IdentifierToken,
  QuotedToken,
  RangeToken,
  Value,
  Expression,
  Hash,
  // Custom-tag authoring surface. Re-exported (rather than deep-imported at the call
  // site) so registering a tag doesn't need a `liquidjs` import, which the
  // noRestrictedImports rule reserves for this wrapper.
  Emitter,
  TagImpl,
  TagImplOptions,
};
export { Context, Tokenizer, TypeGuards };
